import { 
    Card, Grid, Icon, IconButton, Switch, Autocomplete, Chip, Tooltip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { errorAlert, successAlert } from "utils/Functions";
import { get, post, postNoResponse } from "utils/WebService";
import { categoriaOcorrenciaEnum } from "utils/Enums";
import { useUser } from "context/UserContext";
import { useMaterialUIController } from "context";
import { TIPO_PERFIL, temTipoPerfil } from "utils/Perfis";
import { ESCURO } from "assets/coresTema";

export default function TipoOcorrenciaCreateEdit({ id, closeCreateEdit }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const { userPerfils } = useUser();
    const isAdminOrSuporte = temTipoPerfil(userPerfils, TIPO_PERFIL.ADMINISTRADOR, TIPO_PERFIL.SUPORTE);
    const canEditAllPrazos = temTipoPerfil(userPerfils, TIPO_PERFIL.GESTOR, TIPO_PERFIL.ADMINISTRADOR, TIPO_PERFIL.SUPORTE);

    const availableCategories = isAdminOrSuporte ? categoriaOcorrenciaEnum : categoriaOcorrenciaEnum.filter(c => c.code === 1);

    const todasSituacoesFicha = [
        { code: null, label: "Nenhuma (Sem alteração)" },
        { code: 1, label: "Devolvido" },
        { code: 2, label: "Cobrança" },
        { code: 3, label: "Negociação" },
        { code: 4, label: "Pesquisa" },
        { code: 5, label: "Suspenso" },
        { code: 6, label: "Migrado" },
        { code: 7, label: "Quitado" }
    ];

    const availableSituacoesFicha = isAdminOrSuporte 
        ? todasSituacoesFicha 
        : todasSituacoesFicha.filter(s => ![1, 5, 6, 7].includes(s.code));

    const [initialValues, setInitialValues] = useState({
        id: null,
        nome: "",
        cor: "success",
        categoria: 1,
        situacaoFicha: null,
        ativo: true,
        equipesIds: [],
        prazosEquipes: {},
        statusFichaIds: [],
        permiteAgendamento: false,
        prazoMaxAgendamento: 0,
        contatoCliente: false
    });

    const [grupos, setGrupos] = useState([]);
    const [myEquipes, setMyEquipes] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [existingDuplicateRecord, setExistingDuplicateRecord] = useState(null);

    useEffect(() => {
        get("/equipe/list-all").then((res) => {
            setGrupos(res.list || res.data || (Array.isArray(res) ? res : []));
        });

        get("/equipe/minhas-equipes").then((res) => {
            setMyEquipes(res.list || res.data || (Array.isArray(res) ? res : []));
        });

        get("/status-ficha/list-all").then((res) => {
            setStatuses(res.list || res.data || (Array.isArray(res) ? res : []));
        });

        if (id) {
            get(`/tipo-ocorrencia/by-id/${id}`).then((res) => {
                if (res) {
                    setInitialValues({
                        ...res,
                        categoria: res.categoria || 1,
                        situacaoFicha: res.situacaoFicha !== undefined ? res.situacaoFicha : null,
                        equipesIds: res.equipesIds || [],
                        prazosEquipes: res.prazosEquipes || {},
                        statusFichaIds: res.statusFichaIds || []
                    });
                }
            });
        }
    }, [id]);

    const saveRecord = (values, setSubmitting) => {
        postNoResponse("/tipo-ocorrencia/save", values)
            .then(() => {
                successAlert("Tipo de Ocorrência salvo com sucesso!", () => closeCreateEdit());
            })
            // Sem isto, um erro ao salvar não aparecia: a tela ficava parada e parecia ter salvo
            .catch((err) => errorAlert(err?.message ? `Erro ao salvar: ${err.message}` : "Erro ao salvar o Tipo de Ocorrência."))
            .finally(() => {
                if (setSubmitting) setSubmitting(false);
            });
    };

    const handleSubmit = (values, { setSubmitting }) => {
        post("/tipo-ocorrencia/check-duplicate", { id: values.id || "", nome: values.nome })
            .then((res) => {
                const existing = res?.data || res;
                if (existing && existing.id && String(existing.id) !== String(values.id)) {
                    const existingEquipeIds = (existing.equipesIds || []).map(e => String(e));
                    const userEquipeIds = (myEquipes || []).map(me => String(me.id));
                    const userHasEquipeNotInRecord = userEquipeIds.some(ueId => !existingEquipeIds.includes(ueId));

                    if (userHasEquipeNotInRecord || (myEquipes && myEquipes.length > 0 && !userEquipeIds.every(ueId => existingEquipeIds.includes(ueId)))) {
                        setExistingDuplicateRecord(existing);
                        setDuplicateDialogOpen(true);
                        setSubmitting(false);
                        return;
                    } else if (existingEquipeIds.length === 0 && myEquipes && myEquipes.length > 0) {
                        setExistingDuplicateRecord(existing);
                        setDuplicateDialogOpen(true);
                        setSubmitting(false);
                        return;
                    } else {
                        errorAlert(`Já existe um Tipo de Ocorrência cadastrado com o nome "${values.nome}".`);
                        setSubmitting(false);
                        return;
                    }
                }
                saveRecord(values, setSubmitting);
            })
            .catch(() => {
                saveRecord(values, setSubmitting);
            });
    };

    const handleConfirmLinkEquipe = () => {
        if (!existingDuplicateRecord) return;
        setDuplicateDialogOpen(false);

        const currentEquipesIds = existingDuplicateRecord.equipesIds || [];
        const currentIdsStr = currentEquipesIds.map(e => String(e));
        const updatedEquipesIds = [...currentEquipesIds];

        (myEquipes || []).forEach(me => {
            if (!currentIdsStr.includes(String(me.id))) {
                updatedEquipesIds.push(me.id);
            }
        });

        const payloadToSave = {
            ...existingDuplicateRecord,
            equipesIds: updatedEquipesIds
        };

        postNoResponse("/tipo-ocorrencia/save", payloadToSave)
            .then(() => {
                successAlert("Sua equipe foi vinculada ao Tipo de Ocorrência existente com sucesso!", () => closeCreateEdit());
            })
            .catch(() => {
                errorAlert("Erro ao vincular equipe ao Tipo de Ocorrência existente.");
            });
    };

    return (
        <Card id="tipo-ocorrencia-form">
            <MDBox display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1 }}>
                    <MDTypography variant="h5"><Icon>arrow_back</Icon></MDTypography>
                </IconButton>
                <MDTypography variant="h6">{id ? "Editar Tipo de Ocorrência" : "Novo Tipo de Ocorrência"}</MDTypography>
            </MDBox>

            <MDBox component="section" px={3} pb={3}>
                <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
                    {({ values, handleChange, setFieldValue, isSubmitting }) => {
                        const selectedGrupos = grupos.filter(g => (values.equipesIds || []).includes(g.id));
                        const showEquipesPrazos = values.permiteAgendamento && selectedGrupos.length > 0;

                        return (
                            <Form>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <MDInput fullWidth name="nome" label="Nome" variant="standard" value={values.nome || ""} onChange={handleChange} required />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Autocomplete
                                            options={availableCategories}
                                            getOptionLabel={(option) => option.label}
                                            value={availableCategories.find(c => c.code === values.categoria) || availableCategories[0]}
                                            onChange={(event, newValue) => {
                                                const catCode = newValue ? newValue.code : 1;
                                                let newCor = "success";
                                                if (catCode === 2) newCor = "info";
                                                if (catCode === 3) newCor = "warning";
                                                if (catCode === 4) newCor = "error";
                                                if (catCode === 5) newCor = "secondary";
                                                setFieldValue("categoria", catCode);
                                                setFieldValue("cor", newCor);
                                            }}
                                            renderInput={(params) => <MDInput {...params} label="Categoria" variant="standard" required />}
                                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Autocomplete
                                            options={availableSituacoesFicha}
                                            getOptionLabel={(option) => option.label || ""}
                                            value={todasSituacoesFicha.find(s => s.code === values.situacaoFicha) || { code: null, label: "Nenhuma (Sem alteração)" }}
                                            onChange={(event, newValue) => {
                                                setFieldValue("situacaoFicha", newValue ? newValue.code : null);
                                            }}
                                            renderInput={(params) => <MDInput {...params} label="Altera Situação da Ficha para" variant="standard" />}
                                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3} display="flex" alignItems="center" pt={4} pl={2}>
                                        <MDTypography variant="button" fontWeight="regular" color="text">Inativo</MDTypography>
                                        <Switch checked={values.ativo} onChange={(e) => setFieldValue("ativo", e.target.checked)} />
                                        <MDTypography variant="button" fontWeight="regular" color="text">Ativo</MDTypography>
                                    </Grid>
                                    <Grid item xs={12} sm={4} display="flex" alignItems="center" pt={4} pl={2}>
                                        <Switch checked={values.permiteAgendamento} onChange={(e) => setFieldValue("permiteAgendamento", e.target.checked)} />
                                        <MDTypography variant="button" fontWeight="regular" color="text">Permite Agendamento?</MDTypography>
                                    </Grid>
                                    {values.permiteAgendamento && (
                                        <Grid item xs={12} sm={4}>
                                            <MDInput 
                                                fullWidth 
                                                type="number" 
                                                name="prazoMaxAgendamento" 
                                                label="Prazo Máx. Padrão (Dias Úteis)" 
                                                variant="standard" 
                                                value={values.prazoMaxAgendamento || 0} 
                                                onChange={handleChange} 
                                                required
                                            />
                                        </Grid>
                                    )}
                                    {/* Ex.: e-mail ou ligação para o suporte do cliente: o contato não é do devedor */}
                                    <Grid item xs={12} sm={4} display="flex" alignItems="center" pt={4} pl={2}>
                                        <Switch checked={Boolean(values.contatoCliente)} onChange={(e) => setFieldValue("contatoCliente", e.target.checked)} />
                                        <MDBox>
                                            <MDTypography variant="button" fontWeight="regular" color="text" display="block">Contato com o cliente?</MDTypography>
                                            <MDTypography variant="caption" color="text" display="block">
                                                O telefone/e-mail informado fica só na ocorrência e não vai para a ficha do devedor.
                                            </MDTypography>
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <Autocomplete
                                            multiple
                                            options={grupos}
                                            getOptionLabel={(option) => option.nome || option.ccNome || option.id}
                                            value={selectedGrupos}
                                            onChange={(event, newValue) => {
                                                let finalSelection = newValue;
                                                if (!canEditAllPrazos) {
                                                    // Usuário comum não pode remover outras equipes que já estavam vinculadas
                                                    const otherEquipesAlreadySelected = selectedGrupos.filter(g => 
                                                        !myEquipes.some(me => String(me.id) === String(g.id))
                                                    );
                                                    otherEquipesAlreadySelected.forEach(otherG => {
                                                        if (!finalSelection.some(sel => String(sel.id) === String(otherG.id))) {
                                                            finalSelection.push(otherG);
                                                        }
                                                    });
                                                }
                                                const newIds = finalSelection.map(v => v.id);
                                                setFieldValue("equipesIds", newIds);
                                                if (values.prazosEquipes) {
                                                    const updatedPrazos = { ...values.prazosEquipes };
                                                    Object.keys(updatedPrazos).forEach(k => {
                                                        if (!newIds.includes(k)) {
                                                            delete updatedPrazos[k];
                                                        }
                                                    });
                                                    setFieldValue("prazosEquipes", updatedPrazos);
                                                }
                                            }}
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => {
                                                    const isOtherEquipe = !canEditAllPrazos && !myEquipes.some(me => String(me.id) === String(option.id));
                                                    const tagProps = getTagProps({ index });
                                                    return (
                                                        <Chip
                                                            key={option.id}
                                                            label={option.nome || option.ccNome || option.id}
                                                            size="small"
                                                            color={isOtherEquipe ? "default" : "info"}
                                                            {...tagProps}
                                                            onDelete={isOtherEquipe ? undefined : tagProps.onDelete}
                                                        />
                                                    );
                                                })
                                            }
                                            renderInput={(params) => <MDInput {...params} label="Grupos com Acesso (Deixe vazio para Todos/Apenas Admins)" variant="standard" placeholder="Selecione..." />}
                                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                                        />
                                    </Grid>

                                    {showEquipesPrazos && (
                                        <Grid item xs={12}>
                                            <MDBox 
                                                p={2.5} 
                                                borderRadius="lg" 
                                                sx={{ 
                                                    backgroundColor: darkMode ? ESCURO.painel : "#f8f9fa",
                                                    border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.08)"}`
                                                }}
                                            >
                                                <MDBox mb={1} display="flex" alignItems="center" gap={1}>
                                                    <Icon fontSize="small" color="info">schedule</Icon>
                                                    <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                                        Prazos de Agendamento por Equipe
                                                    </MDTypography>
                                                </MDBox>
                                                <MDTypography variant="caption" color={darkMode ? "white" : "secondary"} display="block" mb={2}>
                                                    Defina o prazo de agendamento específico para cada equipe vinculada. Se deixado em branco ou zerado, a equipe herdará o <strong>Prazo Padrão de {values.prazoMaxAgendamento || 0} dias úteis</strong>.
                                                </MDTypography>

                                                <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
                                                    <Table size="small" sx={{ width: "100%", tableLayout: "fixed", display: "table" }}>
                                                        <TableHead sx={{ display: "table-header-group", width: "100%", p: 0 }}>
                                                            <TableRow sx={{ display: "table-row", width: "100%", borderBottom: `2px solid ${darkMode ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.1)"}` }}>
                                                                <TableCell align="left" sx={{ display: "table-cell", width: "45%", color: darkMode ? "#ffffff" : "inherit", fontWeight: "bold", fontSize: "0.75rem", pb: 1, pl: 1 }}>
                                                                    Equipe
                                                                </TableCell>
                                                                <TableCell align="center" sx={{ display: "table-cell", width: "30%", color: darkMode ? "#ffffff" : "inherit", fontWeight: "bold", fontSize: "0.75rem", pb: 1 }}>
                                                                    Prazo Customizado (Dias Úteis)
                                                                </TableCell>
                                                                <TableCell align="center" sx={{ display: "table-cell", width: "25%", color: darkMode ? "#ffffff" : "inherit", fontWeight: "bold", fontSize: "0.75rem", pb: 1, pr: 1 }}>
                                                                    Prazo Vigente
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody sx={{ display: "table-row-group", width: "100%" }}>
                                                            {selectedGrupos.map((g) => {
                                                                const customValue = values.prazosEquipes?.[g.id];
                                                                const hasCustom = customValue !== undefined && customValue !== null && customValue !== "" && Number(customValue) > 0;
                                                                const activeDays = hasCustom ? Number(customValue) : (values.prazoMaxAgendamento || 0);

                                                                const isMyEquipe = myEquipes.some(me => String(me.id) === String(g.id));
                                                                const canEditThisRow = canEditAllPrazos || isMyEquipe;

                                                                return (
                                                                    <TableRow 
                                                                        key={g.id}
                                                                        sx={{ 
                                                                            display: "table-row",
                                                                            width: "100%",
                                                                            borderBottom: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
                                                                            "&:last-child": { borderBottom: 0 }
                                                                        }}
                                                                    >
                                                                        <TableCell align="left" sx={{ display: "table-cell", width: "45%", verticalAlign: "middle", py: 1.5, pl: 1 }}>
                                                                            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                                                <MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>
                                                                                    {g.nome || g.ccNome || g.id}
                                                                                </MDTypography>
                                                                                {isMyEquipe && (
                                                                                    <Chip 
                                                                                        label="Sua Equipe" 
                                                                                        size="small" 
                                                                                        color="info" 
                                                                                        variant="outlined" 
                                                                                        sx={{ height: 22, fontSize: "0.68rem", fontWeight: "bold" }} 
                                                                                    />
                                                                                )}
                                                                                {!canEditThisRow && (
                                                                                    <Tooltip title="Apenas membros desta equipe ou gestores podem alterar este prazo" arrow>
                                                                                        <Chip 
                                                                                            label="Somente Leitura" 
                                                                                            size="small" 
                                                                                            variant="outlined" 
                                                                                            sx={{ 
                                                                                                height: 22, 
                                                                                                fontSize: "0.68rem", 
                                                                                                color: darkMode ? "rgba(255,255,255,0.6)" : "text.secondary", 
                                                                                                borderColor: darkMode ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)",
                                                                                                cursor: "help"
                                                                                            }} 
                                                                                        />
                                                                                    </Tooltip>
                                                                                )}
                                                                            </MDBox>
                                                                        </TableCell>
                                                                        <TableCell align="center" sx={{ display: "table-cell", width: "30%", verticalAlign: "middle", py: 1.5 }}>
                                                                            <MDBox display="flex" justifyContent="center">
                                                                                {canEditThisRow ? (
                                                                                    <MDInput
                                                                                        type="number"
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        placeholder={`Padrão (${values.prazoMaxAgendamento || 0})`}
                                                                                        value={customValue ?? ""}
                                                                                        onChange={(e) => {
                                                                                            const val = e.target.value === "" ? "" : Number(e.target.value);
                                                                                            setFieldValue("prazosEquipes", {
                                                                                                ...values.prazosEquipes,
                                                                                                [g.id]: val
                                                                                            });
                                                                                        }}
                                                                                        inputProps={{ min: 0, style: { textAlign: "center", padding: "6px 10px", color: darkMode ? "#ffffff" : "inherit" } }}
                                                                                        sx={{ width: 130 }}
                                                                                    />
                                                                                ) : (
                                                                                    <Tooltip title="Apenas membros desta equipe ou gestores podem alterar este prazo" arrow>
                                                                                        <span>
                                                                                            <MDInput
                                                                                                type="number"
                                                                                                size="small"
                                                                                                variant="outlined"
                                                                                                disabled
                                                                                                placeholder={`Padrão (${values.prazoMaxAgendamento || 0})`}
                                                                                                value={customValue ?? ""}
                                                                                                inputProps={{ min: 0, style: { textAlign: "center", padding: "6px 10px", color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" } }}
                                                                                                sx={{ width: 130, cursor: "not-allowed" }}
                                                                                            />
                                                                                        </span>
                                                                                    </Tooltip>
                                                                                )}
                                                                            </MDBox>
                                                                        </TableCell>
                                                                        <TableCell align="center" sx={{ display: "table-cell", width: "25%", verticalAlign: "middle", py: 1.5, pr: 1 }}>
                                                                            <MDBox display="flex" justifyContent="center">
                                                                                {hasCustom ? (
                                                                                    <Chip 
                                                                                        label={`${activeDays} dias úteis`} 
                                                                                        size="small" 
                                                                                        color="info" 
                                                                                        variant="filled" 
                                                                                        sx={{ fontWeight: "medium" }}
                                                                                    />
                                                                                ) : (
                                                                                    <Chip 
                                                                                        label={`Padrão (${activeDays} dias)`} 
                                                                                        size="small" 
                                                                                        variant="outlined" 
                                                                                        sx={{ 
                                                                                            color: darkMode ? "#e0e0e0" : "text.secondary", 
                                                                                            borderColor: darkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)" 
                                                                                        }} 
                                                                                    />
                                                                                )}
                                                                            </MDBox>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </MDBox>
                                        </Grid>
                                    )}

                                    <Grid item xs={12} sm={12}>
                                        <Autocomplete
                                            multiple
                                            options={statuses}
                                            getOptionLabel={(option) => option.ccNome || option.nome || option.id}
                                            value={statuses.filter(s => (values.statusFichaIds || []).includes(s.id))}
                                            onChange={(event, newValue) => {
                                                setFieldValue("statusFichaIds", newValue.map(v => v.id));
                                            }}
                                            renderInput={(params) => <MDInput {...params} label="Status Vinculados (Deixe vazio para Todos)" variant="standard" placeholder="Selecione..." />}
                                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                                        />
                                    </Grid>
                                </Grid>

                                <MDBox 
                                    display="flex" 
                                    justifyContent="flex-end" 
                                    mt={4} 
                                    pt={2}
                                    sx={{ borderTop: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0" }}
                                >
                                    <MDButton 
                                        variant="outlined" 
                                        color="secondary" 
                                        onClick={closeCreateEdit} 
                                        sx={{ 
                                            mr: 2, 
                                            color: darkMode ? "#ffffff !important" : undefined, 
                                            borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined,
                                            backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : undefined,
                                            "&:hover": {
                                                borderColor: darkMode ? "#ffffff !important" : undefined,
                                                backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : undefined
                                            }
                                        }}
                                    >
                                        Cancelar
                                    </MDButton>
                                    <MDButton 
                                        variant="contained" 
                                        color="info" 
                                        type="submit" 
                                        disabled={isSubmitting}
                                        sx={{
                                            backgroundColor: darkMode ? "#007bff !important" : undefined,
                                            color: "#ffffff !important",
                                            fontWeight: "bold",
                                            boxShadow: darkMode ? "0 4px 10px rgba(0, 123, 255, 0.3)" : undefined,
                                            "&:hover": {
                                                backgroundColor: darkMode ? "#0069d9 !important" : undefined
                                            }
                                        }}
                                    >
                                        Salvar
                                    </MDButton>
                                </MDBox>
                            </Form>
                        );
                    }}
                </Formik>
            </MDBox>

            {/* Modal de Confirmação de Vinculação de Equipe a Tipo de Ocorrência Existente */}
            <Dialog 
                open={duplicateDialogOpen} 
                onClose={() => setDuplicateDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? ESCURO.painel : "#ffffff",
                        color: darkMode ? "#ffffff" : "inherit"
                    }
                }}
            >
                <DialogTitle sx={{ color: darkMode ? "#ffffff" : "inherit" }}>Tipo de Ocorrência Já Cadastrado</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color={darkMode ? "white" : "text"}>
                        O Tipo de Ocorrência <strong>"{existingDuplicateRecord?.nome}"</strong> já existe no sistema, mas não está associado à sua equipe.
                    </MDTypography>
                    <br/>
                    <MDTypography variant="body2" color={darkMode ? "white" : "text"}>
                        Deseja vincular a sua equipe a este registro existente?
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton 
                        onClick={() => setDuplicateDialogOpen(false)} 
                        variant="outlined" 
                        color="secondary"
                        sx={{
                            color: darkMode ? "#ffffff !important" : undefined,
                            borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                        }}
                    >
                        Não (Cancelar)
                    </MDButton>
                    <MDButton 
                        onClick={handleConfirmLinkEquipe} 
                        color="info" 
                        variant="contained"
                        sx={{
                            backgroundColor: darkMode ? "#007bff !important" : undefined,
                            color: "#ffffff !important",
                            fontWeight: "bold"
                        }}
                    >
                        Sim (Vincular Equipe)
                    </MDButton>
                </DialogActions>
            </Dialog>
        </Card>
    );
}
